import React from 'react';

function QuestionPapersEditor({ pageContent, setPageContent, uploading, uploadingField, addTableRow, deleteTableRow, updateTableRow }) {
  
  const handleFileUpload = async (itemId, file) => {
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      alert('❌ Only PDF files are allowed for question papers.');
      return;
    }
    
    try {
      const { uploadFile } = await import('../../firebase/firebase-config');
      const fileData = await uploadFile(file, `questionPapers/${itemId}_${Date.now()}_${file.name}`);
      
      const newPapers = (pageContent.questionPapers || []).map(paper => 
        paper.id === itemId ? { ...paper, file: fileData, fileName: file.name } : paper
      );
      setPageContent({ ...pageContent, questionPapers: newPapers });
      alert('✅ Question paper uploaded successfully!');
    } catch (error) {
      alert('❌ Error uploading file: ' + error.message);
    }
  };

  const handleDeleteFile = async (itemId, fileUrl) => {
    if (!fileUrl) return;
    
    if (window.confirm('Delete this question paper permanently?')) {
      try {
        const { deleteFileFromStorage } = await import('../../firebase/firebase-config');
        await deleteFileFromStorage(fileUrl);
        
        const newPapers = (pageContent.questionPapers || []).map(paper => 
          paper.id === itemId ? { ...paper, file: null, fileName: null } : paper
        );
        setPageContent({ ...pageContent, questionPapers: newPapers });
        alert('✅ File deleted successfully!');
      } catch (error) {
        alert('❌ Error deleting file: ' + error.message);
      }
    }
  };

  const examTypes = ['Annual', 'Half-Yearly', 'Quarterly', 'Pre-Board', 'Monthly Test'];
  const allClasses = ['Nursery', 'LKG', 'UKG', ...Array(12).fill().map((_, i) => `Class ${i + 1}`), 'Class 12'];
  const subjects = ['Mathematics', 'Science', 'English', 'Hindi', 'Sanskrit', 'Social Studies', 'Computer Science', 'Physics', 'Chemistry', 'Biology', 'Economics', 'Accountancy', 'Business Studies'];

  return (
    <div className="page-editor">
      <div className="editor-card">
        <h3>📝 Question Papers Manager</h3>
        <p className="editor-description">Upload previous year question papers (PDF only). Students can download and practice.</p>

        <div className="form-group">
          <label>Page Title</label>
          <input 
            value={pageContent.title || 'Previous Year Question Papers'} 
            onChange={(e) => setPageContent({...pageContent, title: e.target.value})} 
            className="form-input" 
          />
        </div>

        <div className="form-group">
          <label>Question Papers</label>
          {(pageContent.questionPapers || []).map((q) => (
            <div key={q.id} className="paper-item">
              <div className="paper-fields">
                <select 
                  value={q.class || ''} 
                  onChange={(e) => updateTableRow('questionPapers', q.id, 'class', e.target.value)}
                  className="form-input"
                  style={{flex: 1}}
                >
                  <option value="">Select Class</option>
                  {allClasses.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                
                <select 
                  value={q.subject || ''} 
                  onChange={(e) => updateTableRow('questionPapers', q.id, 'subject', e.target.value)}
                  className="form-input"
                  style={{flex: 1}}
                >
                  <option value="">Select Subject</option>
                  {subjects.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                
                <input 
                  type="text" 
                  placeholder="Year (e.g., 2024)" 
                  value={q.year || ''} 
                  onChange={(e) => updateTableRow('questionPapers', q.id, 'year', e.target.value)} 
                  style={{width: 100}} 
                  className="form-input" 
                />
                
                <select 
                  value={q.examType || ''} 
                  onChange={(e) => updateTableRow('questionPapers', q.id, 'examType', e.target.value)}
                  className="form-input"
                  style={{width: 130}}
                >
                  <option value="">Select Exam</option>
                  {examTypes.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
              
              {q.file && q.file.url && (
                <div className="file-info">
                  <a href={q.file.url} target="_blank" rel="noopener noreferrer" className="file-link">
                    📄 {q.fileName || 'Question Paper'}
                  </a>
                  <button 
                    className="remove-btn-small" 
                    onClick={() => handleDeleteFile(q.id, q.file.url)}
                  >
                    🗑️ Delete File
                  </button>
                </div>
              )}
              
              <div className="upload-row">
                <label className="upload-label">
                  {uploading && uploadingField === `file_${q.id}` ? '⏳ Uploading...' : '📎 Upload PDF'}
                  <input 
                    type="file" 
                    accept=".pdf" 
                    style={{display: 'none'}} 
                    onChange={(e) => { 
                      if (e.target.files[0]) handleFileUpload(q.id, e.target.files[0]); 
                    }} 
                  />
                </label>
                <button 
                  className="remove-btn" 
                  onClick={() => deleteTableRow('questionPapers', q.id)}
                >
                  🗑️ Remove Paper Entry
                </button>
              </div>
            </div>
          ))}
          
          <button 
            className="add-btn" 
            onClick={() => addTableRow('questionPapers', { 
              class: '', 
              subject: '', 
              year: new Date().getFullYear(), 
              examType: 'Annual',
              file: null,
              fileName: null
            })}
          >
            + Add Question Paper
          </button>
        </div>

        <div className="form-group">
          <label>Statistics</label>
          <div style={{background: '#f8fafc', padding: 16, borderRadius: 8, fontSize: 14}}>
            <p><strong>📚 Total Papers:</strong> {(pageContent.questionPapers || []).length}</p>
            <p><strong>📄 Papers with Files:</strong> {(pageContent.questionPapers || []).filter(p => p.file).length}</p>
            <p><strong>📋 Subjects Available:</strong> {[...new Set((pageContent.questionPapers || []).map(p => p.subject).filter(Boolean))].length}</p>
          </div>
        </div>
      </div>

      <style>{`
        .editor-description {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .paper-item {
          border: 1px solid #e2e8f0;
          padding: 15px;
          margin-bottom: 15px;
          border-radius: 12px;
          background: #fafcff;
        }

        .paper-fields {
          display: grid;
          grid-template-columns: 1fr 1fr auto auto;
          gap: 10px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .file-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          background: #f1f5f9;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        .file-link {
          color: #1a3a6b;
          text-decoration: none;
          font-size: 13px;
        }

        .upload-row {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .remove-btn-small {
          background: #ef4444;
          color: white;
          padding: 4px 12px;
          border-radius: 6px;
          border: none;
          font-size: 11px;
          cursor: pointer;
        }

        @media (max-width: 768px) {
          .paper-fields {
            grid-template-columns: 1fr;
          }
          
          .upload-row {
            flex-direction: column;
          }
          
          .upload-row button,
          .upload-row label {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}

export default QuestionPapersEditor;